import { keepPreviousData, queryOptions, useQuery, useSuspenseQuery } from '@tanstack/react-query';

import { api } from '@/lib/api-client';
import { QueryConfig } from '@/lib/tanstack-query';

import z from 'zod';
import { Participation } from '@/types/api/participation';

const ListParticipationsIncludeSchema = z.enum(['user']);

export const ListParticipationsParamsSchema = z.object({
  include: z.array(ListParticipationsIncludeSchema).optional(),
});

export type ListParticipationsParams = z.infer<typeof ListParticipationsParamsSchema>;

export const listParticipations = async ({
  saleId,
  params,
}: {
  saleId: string;
  params?: ListParticipationsParams;
}): Promise<Participation[]> => {
  return (await api.get(`/sales/${saleId}/participations`, { params })).data;
};

export const listParticipationsQueryOptions = (saleId: string, params: ListParticipationsParams = {}) => {
  return queryOptions({
    queryKey: ['sales', saleId, 'participations', params],
    queryFn: () => listParticipations({ saleId, params }),
    enabled: Boolean(params),
    placeholderData: keepPreviousData,
  });
};

type UseParticipationsOptions = {
  params?: ListParticipationsParams;
  saleId: string;
  queryConfig?: QueryConfig<typeof listParticipationsQueryOptions>;
};

export const useParticipations = ({ queryConfig, params, saleId }: UseParticipationsOptions) => {
  return useQuery({
    ...listParticipationsQueryOptions(saleId, params),
    ...queryConfig,
  });
};

export const useParticipationsSuspense = ({ queryConfig, params, saleId }: UseParticipationsOptions) => {
  return useSuspenseQuery({
    ...listParticipationsQueryOptions(saleId, params),
    ...queryConfig,
  });
};
